// Ported from genieos/web/src/utils/historianAlias.ts @ 84923d036
// NOTE (2026-09-15, djin-cloud#806 Ruling Q): comments in the ported region
// below were edited for cloud-context accuracy — the originals asserted a Go
// backend validator path (internal/api/handlers/historian.go) as present
// fact, which is true in GenieOS but does not exist in this repo. The ported
// CODE is unmodified; only comment text changed. See the "Cloud-only
// additions" banner further down for the rest of this file's provenance.
/**
 * Selected-variable alias construction for the Historian Data Viewer.
 *
 * The alias is triple-bound. In GenieOS: it is the label rendered (and
 * user-editable) in the Selected Variables panel, the Recharts series
 * `dataKey` (MultiAxisChart reads `row[v.alias]`), and the SQL `AS` alias
 * sent to `POST /api/v1/historian/query`. djin-cloud's historian rebuild
 * carries the same triple-bound contract into a different stack — an
 * ECharts-based chart (`EChartsMultiLineChart`) and the
 * `POST /api/v1/historian/series-query` endpoint
 * (`backend/src/services/historian-series.service.ts`) — and both are wired
 * to it: `HistorianContext.tsx`'s `buildVariable` — used by `ADD_VARIABLE`
 * and two of the three URL/session seed paths — derives its alias via
 * `sanitizeAlias`/`resolveUniqueAlias` below (not via the two-arg
 * `buildVariableAlias` itself — see that function's own doc comment). That
 * is NOT the only path that produces a `SelectedVariable.alias`, though:
 * the `?q=` persisted-query replay (the `SEED_VARIABLES` dispatch fed
 * by `GET /queries/:id`'s response) seeds each alias verbatim from the
 * server's `columnMeta` keys — already validated server-side when the spec
 * was persisted, but not through either function below — and the
 * `sessionStorage` restore (`isPersistedVariable`) accepts any
 * `typeof alias === 'string'` with no validation at all. Renaming an
 * existing variable goes through the separate `UPDATE_VARIABLE_ALIAS`
 * reducer case, which does call `sanitizeAlias`/`resolveUniqueAlias`.
 * Wherever an alias comes from, `MultiAxisChart` reads `row[v.alias]` as
 * the ECharts `dataKey`, and the series-query request body carries it as
 * `series[].alias`. (djin-cloud's own `POST /api/v1/historian/query` route
 * takes no caller-supplied alias at all — its `AS time`/`AS device_id`/
 * `AS unit` are fixed system column names, not user-chosen labels — so
 * nothing about that route's contract applies here.)
 *
 * This charset originates with the GenieOS backend validator (genieos
 * `internal/api/handlers/historian.go`, `isValidAlias`): letters, digits,
 * underscore, dot and hyphen only, 1..64 characters. In GenieOS a single
 * invalid alias fails the entire query, not just its own series. djin-cloud's
 * `validateSpec` (`backend/src/services/historian-series.service.ts`)
 * enforces the identical charset via its own `ALIAS_RE`, with the same
 * fail-whole-query behavior — an invalid alias 400s the whole
 * `/series-query` request, not just its own series.
 *
 * Before Demsv1.0#2786 the alias was always `${deviceId}_${register}`. That is
 * unreadable for meters, whose TDengine tag is the device's Postgres UUID —
 * a recloser charted as "2dd2bad5-d5e9-405d-88a1-c26c363f23be_frequency". The
 * human name is already resolved by the ADR-011 §6 JOIN and shown in the browser
 * tree; this helper carries it into the selection without letting an operator-
 * entered device name (which may contain spaces or punctuation) break the query.
 */

/** Alias length limit — originates with GenieOS's `isValidAlias` (genieos
 * `internal/api/handlers/historian.go`); also enforced server-side in
 * djin-cloud by `validateSpec`'s `ALIAS_RE`
 * (`backend/src/services/historian-series.service.ts`). */
export const ALIAS_MAX_LENGTH = 64;

/** Characters GenieOS's `isValidAlias` accepts — the same set `validateSpec`'s
 * `ALIAS_RE` enforces server-side in djin-cloud
 * (`backend/src/services/historian-series.service.ts`). */
const UNSAFE_ALIAS_CHARS = /[^A-Za-z0-9_.-]/g;

/** Replace every character outside the alias charset above with an underscore. */
function toAliasPart(value: string): string {
  return value.replace(UNSAFE_ALIAS_CHARS, '_');
}

/**
 * Build the alias for a selected variable from a device label and a column name.
 *
 * The device part is trimmed first so two registers on the same device never
 * collapse to the same alias. In GenieOS that collision would make one
 * series silently shadow the other in the Recharts row keyed by
 * `row[v.alias]`. djin-cloud's chart and series-query request ARE wired to
 * `SelectedVariable.alias` (see the top-of-file comment) — but not through
 * this two-arg function specifically: `HistorianContext.tsx`'s
 * `buildVariable` builds its own `${siteShort}_${deviceId}_${metric}`
 * candidate and runs it through `sanitizeAlias`/`resolveUniqueAlias` below
 * instead, so `buildVariableAlias` has no production call site in this repo
 * (only its own test file calls it). The same uniqueness requirement carries
 * forward regardless of which construction a caller uses. If a register
 * alone exceeds the 64-char budget, the result is hard-capped at 64
 * characters — in GenieOS an over-length alias fails the whole query, which
 * is worse than a truncated register. djin-cloud's `validateSpec` now
 * enforces the identical 64-char/charset rule server-side (see the
 * top-of-file comment). The same truncate-rather-than-fail trade-off holds
 * for `sanitizeAlias`/`resolveUniqueAlias` below — the functions actually
 * wired to the server — since this function itself never reaches the
 * server at all (no production call site, see above).
 */
export function buildVariableAlias(deviceLabel: string, register: string): string {
  const safeRegister = toAliasPart(register);
  const safeDevice = toAliasPart(deviceLabel);
  const deviceBudget = Math.max(0, ALIAS_MAX_LENGTH - safeRegister.length - 1);
  const alias = `${safeDevice.slice(0, deviceBudget)}_${safeRegister}`;
  return alias.slice(0, ALIAS_MAX_LENGTH);
}

// ============================================================================
// Cloud-only additions — NOT present in the genieos source this file was
// ported from (@ 84923d036). genieos resolves alias uniqueness from an
// already-constructed `SelectedVariable` object via a private helper in
// `contexts/HistorianContext.tsx`. djin-cloud's Historian needs the same
// string-keyed sanitizer + resolver from TWO call sites with different
// timing (djin-cloud#806 Task 12): `HistorianContext.tsx`'s `buildVariable`
// (called by `ADD_VARIABLE` and two of the three URL/session seed paths)
// runs it on a bare candidate string *before* the `SelectedVariable` it
// will become exists at all, and the `UPDATE_VARIABLE_ALIAS` reducer case
// runs it on a bare replacement string to rename the alias on a
// `SelectedVariable` that already exists. Either way it needed a different
// signature than genieos's — that is `sanitizeAlias` + `resolveUniqueAlias`
// below. Added here, not a new file, because both build directly on
// `ALIAS_MAX_LENGTH` and `toAliasPart` above.
// ============================================================================

/**
 * Sanitize an arbitrary string into a valid historian alias.
 *
 * Guarantees the result always matches the charset GenieOS's `isValidAlias`
 * enforces (genieos `internal/api/handlers/historian.go`) and that
 * djin-cloud's `validateSpec` also enforces server-side
 * (`backend/src/services/historian-series.service.ts`'s `ALIAS_RE`) —
 * `^[A-Za-z0-9_.-]{1,64}$` — by construction:
 *   - every character outside that set becomes `_` (the same substitution
 *     `buildVariableAlias` uses via `toAliasPart`, so a dot/hyphen/underscore
 *     already in the input passes through unchanged);
 *   - the result is clamped to `ALIAS_MAX_LENGTH`;
 *   - the one input `toAliasPart` cannot turn into a non-empty string — an
 *     empty string, since substitution never removes a character — falls
 *     back to the fixed placeholder `'alias'`, so the `{1,64}` lower bound
 *     is never violated. A string of *only* disallowed characters is NOT
 *     this case: each character is replaced, not dropped, so `'!!!'`
 *     sanitizes to `'___'`, not to empty.
 */
export function sanitizeAlias(s: string): string {
  const cleaned = toAliasPart(s).slice(0, ALIAS_MAX_LENGTH);
  return cleaned.length > 0 ? cleaned : 'alias';
}

/** The isValidAlias charset (see the top-of-file comment for its GenieOS-vs-djin-cloud
 * enforcement status) — mirrored here so resolveUniqueAlias can prove its own output. */
const ALIAS_CHARSET_RE = /^[A-Za-z0-9_.-]{1,64}$/;

/**
 * Resolve `base` to an alias that is not already in `taken`.
 *
 * Callers are expected to pass an already-`sanitizeAlias`-ed `base` (that is
 * the call order both `HistorianContext.tsx`'s `buildVariable` and its
 * `UPDATE_VARIABLE_ALIAS` reducer case use, djin-cloud#806 Task 12);
 * `sanitizeAlias` is still applied defensively here so the charset/length
 * guarantee holds even if a caller skips that step — on an already-sanitized
 * `base` this is a no-op, so "returns `base` when free" holds in the
 * documented usage.
 *
 * On collision, appends a numeric suffix (`_2`, `_3`, ...) and increments
 * until the candidate is free. The suffix is budgeted *before* truncating
 * `base`, never appended past `ALIAS_MAX_LENGTH` — a `base` already at the
 * 64-character limit is shortened to make room, so the result always
 * satisfies the same charset/length contract as `sanitizeAlias`.
 */
export function resolveUniqueAlias(base: string, taken: Set<string>): string {
  const sanitizedBase = sanitizeAlias(base);
  if (!taken.has(sanitizedBase)) {
    return sanitizedBase;
  }

  let n = 2;
  let candidate: string;
  do {
    const suffix = `_${n}`;
    candidate = sanitizedBase.slice(0, ALIAS_MAX_LENGTH - suffix.length) + suffix;
    n += 1;
  } while (taken.has(candidate));

  // Defensive: the loop above is constructed to always satisfy the charset,
  // but assert it rather than trust it silently — a future edit to the
  // suffix scheme that broke this would otherwise ship a query-breaking
  // alias. `validateSpec`'s `ALIAS_RE`
  // (`backend/src/services/historian-series.service.ts`) would now catch it
  // too and 400 the whole `/series-query` request, but only after the alias
  // was already sent and the request round-tripped — this assertion is what
  // stops it locally, before that.
  if (!ALIAS_CHARSET_RE.test(candidate)) {
    throw new Error(`resolveUniqueAlias produced an invalid alias: ${candidate}`);
  }
  return candidate;
}
